import { CircularProgress } from '@mui/material';
import ConfirmCodePaper from 'containers/login/login-flow/ConfirmCodePaper';
import { useSignInProcess } from 'hooks/useSignInProcess';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'redux/reducers';
import { useLoginFlow } from 'hooks/useLoginFlow';
import { resetUsersLoginInfo } from 'redux/actions/internal/general/users';
import { Fragment, useEffect, useState } from 'react';
import { resetAllValidationInfo } from 'redux/actions/internal/general/validation';
import firebase, { db } from 'config/firebase';
import PhoneLoginPaper from './login-flow/PhoneLoginPaper';
import EmailLoginPaper from './login-flow/EmailLoginPaper';
import RegisterInfoPaper from './login-flow/RegisterInfoPaper';
import { useAuth } from 'context/authentication';
import { stateHasFailed } from 'services/stateHelpers';
import { FirestoreErrors } from 'types/firestore';
import { useCartService } from 'services/cart/useCartService';
import useLocalStorage from 'hooks/useLocalStorage';
import Cookies from 'js-cookie';
import { User } from '@firebase/auth-types';
import { ModalPopup } from 'components/molecules/ModalPopup';
import { Box } from '@mui/system';
import { useRest } from 'hooks/useRest';
import { _generateToken } from 'services/cart/useCartService/token';
import GoogleMapsApiContainer from 'containers/google-maps/GoogleMapsApiContainer';
import { LoginContextProvider } from 'context/login-context';
import RecognizedPaper from './login-flow/RecognizedPaper';
import { isClientSide, isServerSide } from 'services/utils';

declare global {
    interface Window {
        confirmationResult: firebase.auth.ConfirmationResult;
        googleUserIdToken: string;
        googleUserAccessToken: string;
        authCredential: firebase.auth.AuthCredential;
    }
}

interface ILoginModalProps {
    open?: boolean;
    handleClose?: () => void;
    inAppBrowserDetected?: boolean;
}

const LoginModal = ({ open, handleClose, inAppBrowserDetected }: ILoginModalProps) => {
    const authentication = useLoginFlow();
    const signInProcess = useSignInProcess();
    const dispatch = useDispatch();
    const currentUser = useSelector((state: RootState) => state.currentUser);
    const { customerClaims } = useRest();

    const { isInitialised, logout, logoutSecondary } = useAuth();

    const cartService = useCartService();
    const [cartId, setCartId] = useLocalStorage('_cart_id');

    useEffect(() => {
        if (stateHasFailed(currentUser)) {
            if (currentUser.errors?.code === FirestoreErrors.DocumentDoesNotExist) {
                if (!authentication.isLoginOpen) logout();
                else if (!signInProcess.hasBeenThrough('REGISTER_INFO')) {
                    signInProcess.nextStep('REGISTER_INFO');
                }
            }
        }
    }, [currentUser.errors]);

    useEffect(() => {
        if (!authentication.isLoginOpen) {
            setTimeout(() => {
                dispatch(resetUsersLoginInfo());
                dispatch(resetAllValidationInfo());
                signInProcess.resetProcess();
            }, 500);
            // authentication.closeLogin();
        }
    }, [authentication.isLoginOpen]);

    const handleModalClose = () => {
        logoutSecondary();
        authentication.closeLogin();
        handleClose?.();
    };

    const handleLogin = async (user: User, register: boolean = false) => {
        if (user) {
            let _cartId = null;
            const _localCart = cartId;
            if (register) {
                const doc = await db.collection('/carts').add({ type: 'delivery' });
                const _token = await _generateToken();
                await customerClaims.post(
                    {
                        userId: user.uid,
                        cartId: doc.id,
                    },
                    { 'x-access-token': _token },
                    null,
                    user.uid,
                    'claims'
                );
                _cartId = doc.id;
            } else {
                // const token = await user.getIdToken(true);
                const tokenResult = await user.getIdTokenResult();
                _cartId = tokenResult.claims.cartId;
                // console.log('user logged in token, tokenResult', token, tokenResult);
            }
            if (!_localCart || _localCart === 'undefined') {
                setCartId(_cartId);
            } else {
                setCartId(undefined);
                cartService.migrate(_cartId).then(() => {
                    setCartId(_cartId);
                });
            }
            Cookies.set('userCart', _cartId, { expires: 1000 });

            handleClose?.();
        }
        logoutSecondary();
    };

    return (
        <ModalPopup open={open || authentication.isLoginOpen} handleClose={handleModalClose} minHeight="auto" minWidth="auto">
            <Box>
                <LoginContextProvider>
                    <GoogleMapsApiContainer>
                        <Fragment>
                            {(!isInitialised && <CircularProgress size={20} />) || (
                                <>
                                    {signInProcess.currentStep === 'LOGIN_WITH_PHONE' && (
                                        <PhoneLoginPaper onLogin={handleLogin} />
                                    )}
                                    {signInProcess.currentStep === 'CONFIRM_CODE' && <ConfirmCodePaper onLogin={handleLogin} />}
                                    {signInProcess.currentStep === 'LOGIN_WITH_EMAIL' && (
                                        <EmailLoginPaper onLogin={handleLogin} inAppBrowserDetected={inAppBrowserDetected} />
                                    )}
                                    {signInProcess.currentStep === 'REGISTER_INFO' && <RegisterInfoPaper onLogin={handleLogin} />}
                                    {signInProcess.currentStep === 'RECOGNIZED' && <RecognizedPaper onLogin={handleLogin} />}
                                </>
                            )}
                        </Fragment>
                    </GoogleMapsApiContainer>
                </LoginContextProvider>
            </Box>
        </ModalPopup>
    );
};

export default LoginModal;
